/**
 * One section = one title.
 *
 * A titled section band already prints THE heading for its region, so every
 * data block composed inside it — chart, figure, table, control surface, story
 * block — must not print its own title too: the page would render the same
 * string twice (section h2 + block h3) and, once the report nav echoes it,
 * three times. The section title and the block title are independent free
 * strings, so the duplication is only visible in the rendered page.
 *
 * This module relates them. A titled section publishes what it printed and at
 * which level; a hosted block resolves its own heading against that instead of
 * printing unconditionally:
 *
 *   - same title as the host  -> the block renders TITLELESS. The section
 *     already said it; saying it again is the defect.
 *   - different title         -> the block renders, DEMOTED below the host, so
 *     it reads as a sub-heading of the section rather than a competing peer.
 *
 * The `hosted` prop states the same conclusion where no published host can
 * reach it: a titled surface that is not an FmSectionBand (a collapse label, a
 * ranked-table section, a route-local wrapper), a ReactNode title with no
 * comparable plain text, or an earlier sibling that already printed the title.
 * `duplicate-section-title` in scripts/design/ci-lint-design-rescue.py is the
 * static half of the same rule, and `hosted` is what satisfies it.
 */
import React from 'react';

/** What a titled section published about the heading it printed. */
export interface FmHostedHeadingHost {
  /** Normalized plain text of the printed title, or null when it is not plain text. */
  title: string | null;
  /** Level the host printed it at; hosted blocks stay strictly below this. */
  level: 1 | 2 | 3 | 4;
}

export type FmHeadingLevel = 2 | 3 | 4 | 5 | 6;

const FmHostedHeadingContext = React.createContext<FmHostedHeadingHost | null>(null);

/** Published by a titled section around its body. DS-internal. */
export const FmHostedHeadingProvider = FmHostedHeadingContext.Provider;

/**
 * Comparable plain text of a heading, or null when the node is not plain text.
 *
 * Null means "not comparable", never "equal": a ReactNode title can carry
 * markup a string can't represent, and guessing equality there would delete a
 * heading the section never printed.
 */
export function plainHeadingText(node: React.ReactNode): string | null {
  if (typeof node === 'number') return String(node);
  if (typeof node !== 'string') return null;
  const normalized = node.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  return normalized.length > 0 ? normalized : null;
}

/**
 * The heading's own words, unchanged, for an `aria-label` on a block that
 * renders titleless. Dropping the visible heading must not leave the region
 * unnamed for a screen reader.
 */
export function headingLabelText(node: React.ReactNode): string | undefined {
  if (typeof node === 'number') return String(node);
  if (typeof node !== 'string') return undefined;
  const trimmed = node.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export interface FmHostedHeadingDecision {
  /** false — the host already printed this title, so the block renders titleless. */
  render: boolean;
  /** Level to render at when `render` is true. Never at or above the host's. */
  level: FmHeadingLevel;
}

function demote(ownLevel: FmHeadingLevel, hostLevel: 1 | 2 | 3 | 4): FmHeadingLevel {
  return Math.min(6, Math.max(ownLevel, hostLevel + 1)) as FmHeadingLevel;
}

/**
 * Resolve a block's heading against the section hosting it. Pure, so the
 * decision is testable without rendering.
 */
export function resolveHostedHeading(args: {
  title: React.ReactNode;
  ownLevel: FmHeadingLevel;
  host: FmHostedHeadingHost | null;
  /**
   * The surrounding surface ALREADY printed this exact title. Stated by the
   * caller for the cases no published host can settle: a host that is not an
   * FmSectionBand, a ReactNode title that has no comparable plain text, or an
   * earlier sibling block that printed the same title.
   */
  hosted?: boolean;
}): FmHostedHeadingDecision {
  if (args.hosted) return { render: false, level: args.ownLevel };
  if (!args.host) return { render: true, level: args.ownLevel };

  const own = plainHeadingText(args.title);
  if (own !== null && args.host.title !== null && own === args.host.title) {
    return { render: false, level: args.ownLevel };
  }
  return { render: true, level: demote(args.ownLevel, args.host.level) };
}

/** Hook form of {@link resolveHostedHeading} for a hosted block. */
export function useHostedHeading(
  title: React.ReactNode,
  ownLevel: FmHeadingLevel,
  hosted?: boolean,
): FmHostedHeadingDecision {
  const host = React.useContext(FmHostedHeadingContext);
  return resolveHostedHeading({ title, ownLevel, host, hosted });
}

/**
 * The host descriptor a titled section publishes, or null when it printed no
 * title (its own host, if any, then still applies to its children).
 */
export function hostedHeadingFor(
  title: React.ReactNode,
  level: 1 | 2 | 3 | 4,
): FmHostedHeadingHost | null {
  // Mirror what SectionBandHeader actually prints: it renders the title under
  // a plain `title ? ... : null`, so '' and 0 print NOTHING. Treating those as
  // a host would demote every child for a heading the band never printed.
  if (!title) return null;
  return { title: plainHeadingText(title), level };
}

/** The heading the nearest titled section printed, or null outside one. */
export function useInheritedHostedHeading(): FmHostedHeadingHost | null {
  return React.useContext(FmHostedHeadingContext);
}

/**
 * The host value a section band publishes around its body.
 *
 * An untitled band must NOT publish `null`: that would override the host it is
 * nested in and let an inner block reprint the outer section's title. It
 * republishes the inherited host instead.
 */
export function useSectionHostedHeading(
  title: React.ReactNode,
  level: 1 | 2 | 3 | 4,
): FmHostedHeadingHost | null {
  const inherited = React.useContext(FmHostedHeadingContext);
  return hostedHeadingFor(title, level) ?? inherited;
}

/**
 * Titles a section must not print again, in order: the section's own title
 * followed by every title already printed by an earlier sibling block.
 *
 * A section renderer holds the whole ordered block list, so it can settle
 * sibling repeats — two adjacent blocks carrying the same title — which no
 * single block can see from inside itself.
 */
export function suppressedSiblingTitles(
  hostTitle: React.ReactNode,
  siblingTitles: readonly React.ReactNode[],
): boolean[] {
  const printed = new Set<string>();
  const host = plainHeadingText(hostTitle);
  if (host !== null) printed.add(host);
  return siblingTitles.map((title) => {
    const text = plainHeadingText(title);
    if (text === null) return false;
    if (printed.has(text)) return true;
    printed.add(text);
    return false;
  });
}
